import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ProfileRow {
  id: string;
  full_name: string | null;
  email: string | null;
  role: string | null;
  company_id: string | null;
  onboarding_checklist_dismissed: boolean | null;
  created_at: string | null;
  // MS388: live Notification cadence controls in /settings read +
  // write this jsonb column. Canonical shape lives in
  // 20260519130000_ms383_profile_notification_prefs.sql. Parsing +
  // defaults are centralised in src/lib/notificationPrefs.ts so this
  // type stays permissive (unknown jsonb) and the parser handles the
  // hard contract.
  notification_prefs: unknown;
}

/**
 * Fetches the authenticated user's profiles row. Null if no session.
 *
 * Shared across almost every page, cache hit rate should be high. Default
 * staleTime (30s from QueryClient defaults) is fine; this row changes rarely.
 */
export function useProfile() {
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: ["profile", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<ProfileRow | null> => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email, role, company_id, onboarding_checklist_dismissed, created_at, notification_prefs")
        .eq("id", userId)
        .single();
      if (error) throw error;
      return (data as ProfileRow | null) ?? null;
    },
  });
}
